/* eslint-disable no-nested-ternary */
/* eslint-disable react/jsx-no-bind */
/* eslint-disable react/no-danger */
import React, { useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { BreakpointContext, useBreakpoints } from 'react-use-breakpoints';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowDownFromLine,
  faArrowUpFromLine,
  faCircle,
  faCircleCheck,
  faCube,
  faCubes,
  faFilterCircleXmark,
  faFilters,
} from '@fortawesome/pro-light-svg-icons';
import { every } from 'lodash';
import { StyledTechTable } from './styles';
import { Badge } from '../Badge';
import { Text } from '../Text';
import { TechThumbnail } from '../TechThumbnail';
import { Tech } from './types';
import { techMock } from './mocks';
import { categoryMapper, categoryToColorMapper } from './constants';
import { SimpleModal } from '../SimpleModal';
import { Slider } from '../Slider';
import { Button } from '../Button';

export interface ITechTable {
  data: Tech[];
  className?: string;
  slider?: boolean;
  showFilters?: boolean;
}

const ITEMS_COUNT = 20;

const filtersInitialState = {
  frontend: false,
  backend: false,
  ai: false,
  api: false,
  cloud: false,
  qa: false,
  database: false,
  devops: false,
  security: false,
  payments: false,
  crm: false,
  cms: false,
  ecommerce: false,
  uiux: false,
  collaboration: false,
};

export const TechTable = ({
  className,
  data,
  slider = true,
  showFilters = true,
}: ITechTable) => {
  const breakpoints = React.useContext(BreakpointContext);
  const { breakpoint } = useBreakpoints(breakpoints);
  const isUnderPhablet = [`watch`, `phone`, `phablet`].some(
    (item) => item === breakpoint,
  );
  const [modalIsOpen, setIsOpen] = React.useState(false);
  const [selectedTech, setSelectedTech] = React.useState<Tech>(techMock);
  const [itemsDisplayed, setItemsDisplayed] = React.useState(ITEMS_COUNT);
  const [filters, setFilters] = useState(filtersInitialState);
  const [isFiltersOpen, setIsFiltersOpen] = useState(!isUnderPhablet);
  const [isLoading, setIsLoading] = useState(false);

  function openModal() {
    setIsOpen(true);
  }

  function closeModal() {
    setIsOpen(false);
  }

  function clearAllFilters() {
    setFilters(filtersInitialState);
  }

  function toggleOpenFilters() {
    setIsFiltersOpen((current) => !current);
  }

  const settingsSlider = {
    slidesToShow: 4,
    slidesToScroll: 4,
    rows: 5,
    infinite: false,
    slidesPerRow: 1,
  };
  const settingsSliderResponsive = {
    rows: 4,
  };

  useEffect(() => {
    if (!slider) {
      return;
    }
    let itemsDisplayedByBreakpoint;
    switch (breakpoint) {
      case `watch`:
      case `phone`: {
        itemsDisplayedByBreakpoint = 4;
        break;
      }
      case `phablet`:
      case `tablet`: {
        itemsDisplayedByBreakpoint = 15;
        break;
      }
      default:
        itemsDisplayedByBreakpoint = 20;
    }
    setItemsDisplayed(itemsDisplayedByBreakpoint);
  }, [breakpoint]);

  const isAllFalse = every(filters, (val) => !val);

  let filtered =
    showFilters && !isAllFalse
      ? data.filter(({ category }) => {
          return category.some((cat): Tech => filters[categoryMapper[cat]]);
        })
      : data;

  const remaining = slider
    ? itemsDisplayed - (filtered.length % itemsDisplayed)
    : 0;

  if (remaining > 0) {
    const additions: Tech[] = Array(remaining).fill(techMock);
    filtered = filtered.concat(additions);
  }

  const filteredItemsToRender = filtered.map((tech: Tech) => {
    const { image, title, category, filler = false } = tech;
    return (
      <Slider.Button
        key={uuidv4()}
        onClick={() => {
          if (filler) {
            return;
          }
          setSelectedTech(tech);
          openModal();
        }}
      >
        <TechThumbnail
          alt={image.title}
          image={image.gatsbyImageData}
          title={title}
          hide={filler}
          rightSide={category.map((cat) => (
            <Badge
              rounded
              noHover
              large
              color={categoryToColorMapper[categoryMapper[cat]]}
              leftSection={<FontAwesomeIcon icon={faCube} />}
            />
          ))}
        />
      </Slider.Button>
    );
  });

  useEffect(() => {
    setIsLoading(false);
  }, [filteredItemsToRender]);

  return (
    <StyledTechTable className={className}>
      {showFilters && (
        <div className="filters">
          <div className="filters__head">
            <div className="filters__heading">
              <FontAwesomeIcon size="lg" icon={faFilters} />
              <Text
                className="filters__title"
                type="body1"
                tag="span"
                color="yellow_2"
                noRhythem
              >
                Filters:
              </Text>
            </div>
            <div className="filters__buttons">
              <Button
                noBg
                contrast
                size="xsmall"
                disabled={false}
                onClick={clearAllFilters}
              >
                <FontAwesomeIcon size="lg" icon={faFilterCircleXmark} />
              </Button>
              <Button
                noBg
                contrast
                size="xsmall"
                disabled={false}
                onClick={toggleOpenFilters}
              >
                <FontAwesomeIcon
                  size="lg"
                  icon={isFiltersOpen ? faArrowUpFromLine : faArrowDownFromLine}
                />
              </Button>
            </div>
          </div>
          <div
            className={`filters__items ${
              isFiltersOpen ? `filters__items--open` : `filters__items--close`
            }`}
          >
            {Object.entries(categoryMapper).map(([displayName, filterName]) => (
              <button
                type="button"
                className="filter__item"
                key={filterName}
                onClick={() => {
                  setIsLoading(true);
                  setFilters({
                    ...filters,
                    [filterName]: !filters[filterName],
                  });
                }}
              >
                <Badge
                  selected={filters[filterName]}
                  color={categoryToColorMapper[filterName]}
                  leftSection={<FontAwesomeIcon icon={faCubes} />}
                  rightSection={
                    filters[filterName] ? (
                      <FontAwesomeIcon icon={faCircleCheck} />
                    ) : (
                      <FontAwesomeIcon icon={faCircle} />
                    )
                  }
                  large
                >
                  {displayName}
                </Badge>
              </button>
            ))}
          </div>
        </div>
      )}

      {slider ? (
        !isLoading && (
          <Slider
            settings={settingsSlider}
            responsivePhoneSettings={settingsSliderResponsive}
            responsivePhabletSettings={settingsSliderResponsive}
          >
            {filteredItemsToRender}
          </Slider>
        )
      ) : (
        <div className="list">{filteredItemsToRender}</div>
      )}

      <SimpleModal
        onClose={closeModal}
        isOpen={modalIsOpen}
        title={selectedTech.title}
        image={selectedTech.image}
        content={selectedTech.description.description}
        rightSide={selectedTech.category.map((cat) => (
          <div>
            <Badge
              rounded
              noHover
              large
              color={categoryToColorMapper[categoryMapper[cat]]}
              leftSection={<FontAwesomeIcon icon={faCube} />}
            />
            <Text noRhythem type="body3" tag="span">
              {cat}
            </Text>
          </div>
        ))}
        footer={
          <>
            <Text noRhythem color="gray_3_65" type="body3" tag="span">
              For more info visit offical
            </Text>
            <Text noRhythem color="pink_2" type="body3" tag="span">
              <a
                target="_blank"
                rel="noopener noreferrer"
                aria-label={selectedTech.url}
                href={selectedTech.url}
              >
                website
              </a>
            </Text>
          </>
        }
      />
    </StyledTechTable>
  );
};
